import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { BizzMineLocalStorageService } from 'src/app/shared/services/localStorage/bizzmine-local-storage.service';
import { SwUpdate } from '@angular/service-worker';
import { distinctUntilChanged,  interval, map, startWith, take, takeWhile } from 'rxjs';
import { Store } from '@ngrx/store';
import { appActions } from 'src/app/store/features/app/app-actions';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NetworkService } from '../network/network.service';
import { UserSettingsApiService } from 'src/app/api/bizzmine/user-settings/user-settings-api.service';
import { addDays } from 'date-fns';
import { pwaHardcodedNgxExtendedPdfViewerUrls } from 'src/app/shared/constants/ngx-extended-pdf-paths';
import { environment } from 'src/environments/environment';
import { DeviceService, OS } from 'src/app/core/services/device/device.service';

export interface CacheVersions {
  current: string | null;
  previous: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  public cacheVersions: CacheVersions = {
    current: null,
    previous: null
  };

  public isOnline: boolean = true;
  private localStorage = inject(BizzMineLocalStorageService);
  private swUpdate = inject(SwUpdate);
  private actions$ = inject(Actions);
  private store$ = inject(Store);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private deviceService = inject(DeviceService);

  private get networkService(): NetworkService {
    return this.injector.get(NetworkService);
  }
  private usersettingsApiService = inject(UserSettingsApiService)
  private maxDaysOfflinePwaUsage: number | undefined;
  private _hasOfflineWorkspace: boolean = false;

  public get hasOfflineWorkspace(): boolean {
    return this._hasOfflineWorkspace;
  }

  public set hasOfflineWorkspace(value: boolean) {
    this._hasOfflineWorkspace = value;
  }

  private hardcodedRoutes: string[] = pwaHardcodedNgxExtendedPdfViewerUrls;

  public pwaInstalled$ = interval(2000).pipe(startWith(0),
    map(() => this.isPwa()),
    distinctUntilChanged(),
    takeWhile(isStandalone => !isStandalone, true) // Continue until the PWA is installed, then emit the last value
  );

  public init(): void {
    this.pwaInstalled$.subscribe((isStandalone) => {
      if(isStandalone){
        this.addSessionVariablesToLocalStorage();
      }
    });

    this.config();
    this.initialize();
  }
  
  private addSessionVariablesToLocalStorage(): void {
    Object.keys(sessionStorage).forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value) {
        this.localStorage.setItem(key, value);
      }
    });
  }



  public initialize(): void {
    if (this.swUpdate.isEnabled) {
      // Check for updates 
      if(this.networkService.isOnline) {
        this.checkForUpdate();
      }

      // Listen if servicewoerk is ready  and add items to cache
      this.checkServiceWorkerReady();
    }

  }

  public setMaxOfflinePwaUsage(): void {
    if(!this.maxDaysOfflinePwaUsage){
        console.error('Max days offline PWA usage is not set.');
      return;
    } 
    this.localStorage.setItem('maxDateOfflinePwaUsage', addDays(Date.now(), this.maxDaysOfflinePwaUsage).toISOString());
  }

  public getMaxDaysOfflinePwaUsage(): void {
    this.usersettingsApiService.getMaxDaysOfflinePwaUsage().pipe(take(1)).subscribe({
        next: (res) => {
            this.maxDaysOfflinePwaUsage = res;
            if(!this.localStorage.getItem('maxDateOfflinePwaUsage')){
              this.localStorage.setItem('maxDateOfflinePwaUsage', addDays(Date.now(), this.maxDaysOfflinePwaUsage).toISOString());
            }
        },
        error: (error) => {
          console.error('Error getting max days offline PWA usage:', error);
        }
      }
    );
  }

  public checkServiceWorkerReady(): void {
      if (window.navigator && navigator.serviceWorker) {
        navigator.serviceWorker.ready.then(sw => {
          console.debug('[ServiceWorker] Ready', sw);
          // Listen for updates
          setTimeout(() => {
            this.addItemsToCache();
          }, 1000);
        });
      }
  }

  public addItemsToCache(): void {
    console.debug('[ServiceWorker] Adding items to cache');
    this.hardcodedRoutes.forEach((route: string) => {
      this.fetchRoute(route);
    });
    
  }
  public checkForUpdate(): void {
    this.swUpdate.checkForUpdate().then((hasUpdate: boolean) => {
      if (hasUpdate) {
        this.installUpdate();
      }
    }).catch((error: any) => {
      console.error('Error checking for update:', error)
      });
  }

  private installUpdate(): void {
    console.debug('Update installing now ...');
    //clear cache
    this.clearPwaCache();

   setTimeout(() => {
     window.location.reload();
   }, 1000);
  }

  public isPwa(): boolean {
    const isPwaInstalled = localStorage.getItem('pwaInstalled') === 'true';
    const isStandalone = ['standalone', 'minimal-ui'].some(
      (displayMode) => window.matchMedia('(display-mode: ' + displayMode + ')').matches);
    if(isStandalone) this.localStorage.setItem('pwaInstalled', 'true');
    return (isPwaInstalled && this.deviceService.getOS() == OS.MacOS) || isStandalone;
  }


  private config(): void {
    this.cacheVersions.current = environment.appVersion;
    this.cacheVersions.previous = this.localStorage.getItem('previousCacheVersion');

    if (this.isVersionUpdated() && this.cacheVersions.previous) {
      //update
      console.debug('Updated to version', this.cacheVersions.current);
    } else {
      //no update
      console.debug('Started version', this.cacheVersions.previous);
    }

    this.appInstalledListener();

    const keepWorkingOffline = this.localStorage.getItem('appOffline');
    if(!keepWorkingOffline){
      this.localStorage.setItem('appOffline', 'false');
    }
    if (this.isPwa() && keepWorkingOffline == 'true') {
      this.store$.dispatch(appActions.offlineModeChanged({ isOnline: false }));
      this.isOnline = false;
    }

    this.actions$.pipe(ofType(appActions.offlineModeChanged), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.isOnline = value.isOnline;
    });

  }

  private isVersionUpdated(): boolean {
    return this.cacheVersions.current !== this.cacheVersions.previous;
  }

  private clearPwaCache(): void {
    console.debug('Clearing cache ...');
    // Delete cached files
    if ('serviceWorker' in navigator) {
      caches.keys().then((cacheNames) => {
        cacheNames.forEach(function (cacheName) {
          caches.delete(cacheName);
        });
      });
    }

    // Remove SW
    if (window.navigator && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    } else {
      console.debug('No SW found');
    }
  }

  public addHardcodedRoute(path: string): void {
    if(!this.hardcodedRoutes.includes(path)) {
      this.hardcodedRoutes.push(path);
    }
  }

  private appInstalledListener(): void{
    window.addEventListener('appinstalled', () => {
      this.localStorage.setItem('pwaInstalled', 'true');
    });
  }

  public fetchRoute(path: string): void {
    const request = new Request(path, { headers: {'Accept': '*/*' }});
    fetch(request).then((res)=> console.debug('item stored', path, res)).catch((error) => {
      console.error('Error adding hardcoded route to cache:', error);
    });
  }


}
